/**
 * VirtualizedNotesList
 * ────────────────────
 * Feb 27, 2026 — Component built specifically to fix the Notes-tab INP
 * lag (~8.5s on busy charts).  Replaces the inline
 * `paginatedNotes.map(...)` blocks in App.js that were rendering
 * hundreds of rows + running an `Array.find()` lookup per row against
 * the `allClientsForLookup` (~3k items) and `users` (~50 items)
 * collections.
 *
 * What this component does:
 *   - Renders rows with `react-window` when the page is large enough
 *     to benefit from virtualization; falls back to a normal map for
 *     small pages so existing CSS / accessibility behaviour stays
 *     untouched.
 *   - Reads client / provider info from O(1) Map lookups
 *     (`clientsByIdMap`, `usersByIdMap`) passed in from the parent
 *     instead of doing an O(N) `find()` per row.
 *   - Two presentations:
 *     • `view="table"` → table rows
 *     • `view="cards"` → card view via the parent-supplied `renderNote`
 *
 * The virtualization threshold is conservative (rowCount >= 50) so
 * small lists keep their semantic HTML table / list ergonomics and
 * we only pay the virtualization overhead when it actually helps.
 */

import React from 'react';
import { List } from 'react-window';
import { Badge } from '../ui/badge';

// Default row heights.  Calibrated against current Notes-tab styling
// (text-sm rows with one-line content + 8px padding).  If padding
// changes later, tweak these constants.
const TABLE_ROW_PX = 38;
const CARD_ROW_PX = 132;

// Virtualize only when the visible page is larger than this — small
// lists keep their plain DOM rendering for accessibility / printing.
const VIRTUALIZE_THRESHOLD = 50;

// Approximate viewport budget for the list.  We cap at 800px so the
// list doesn't push the rest of the Notes tab off-screen on tall
// monitors; pagination handles overflow within that envelope.
const LIST_MAX_HEIGHT = 800;

function rowHeightFor(count, rowHeight) {
  // Show all rows when small enough to fit; else cap.
  return Math.min(LIST_MAX_HEIGHT, count * rowHeight);
}

function getRowDisplayValues(note, clientsByIdMap, usersByIdMap, formatClientName) {
  const noteClient = note.client_id ? clientsByIdMap.get(note.client_id) : null;
  const noteProvider = note.provider_id ? usersByIdMap.get(note.provider_id) : null;
  const noteClients = note.is_group_note && note.client_ids?.length > 0
    ? note.client_ids.map((id) => clientsByIdMap.get(id)).filter(Boolean)
    : (noteClient ? [noteClient] : []);
  const isLocked = note.is_locked || note.status === 'locked';
  const clientDisplayName = noteClients.length > 0
    ? (note.is_group_note
        ? noteClients.map((c) => formatClientName(c)).join(', ')
        : formatClientName(noteClients[0]))
    : note.client_name || null;
  return { noteClient, noteProvider, noteClients, isLocked, clientDisplayName };
}

// ───────────── Table-row component used by react-window ─────────────

const TableRow = ({ index, style, notes, clientsByIdMap, usersByIdMap, formatClientName, formatStatusLabel, getStatusBadgeColor, formatDateStringForDisplay, openNote }) => {
  const note = notes[index];
  if (!note) return null;
  const { isLocked, clientDisplayName, noteProvider } = getRowDisplayValues(
    note, clientsByIdMap, usersByIdMap, formatClientName,
  );
  return (
    <div
      style={{ ...style, display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.4fr 1.4fr 1fr', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}
      className="hover:bg-gray-50 cursor-pointer text-sm"
      onClick={() => openNote(note)}
      data-testid={`virtualized-note-row-${note.id}`}
    >
      <div className="p-2 font-medium truncate">
        {clientDisplayName
          ? clientDisplayName
          : <span className="text-red-600">⚠️ No Client</span>}
        {note.is_group_note && <Badge className="ml-1 text-xs">GROUP</Badge>}
      </div>
      <div className="p-2">
        <Badge className={getStatusBadgeColor(note.status)}>
          {formatStatusLabel(note)}
        </Badge>
        {isLocked && <Badge variant="outline" className="ml-1 text-xs">🔒</Badge>}
      </div>
      <div className="p-2 truncate">{note.template_name || note.title || 'Untitled'}</div>
      <div className="p-2 truncate">{noteProvider?.full_name || 'Unknown'}</div>
      <div className="p-2">{formatDateStringForDisplay(note.date_of_service || note.created_at)}</div>
    </div>
  );
};

const CardRow = ({ index, style, notes, renderNote }) => {
  const note = notes[index];
  if (!note) return null;
  return (
    <div style={style} data-testid={`virtualized-note-card-${note.id}`}>
      <div className="px-1 pb-3">
        {renderNote(note, note.is_locked || note.status === 'locked')}
      </div>
    </div>
  );
};

// ───────────── Public export ─────────────

export const VirtualizedNotesList = ({
  notes,
  view,                          // 'table' | 'cards'
  clientsByIdMap,
  usersByIdMap,
  formatClientName,
  formatStatusLabel,
  getStatusBadgeColor,
  formatDateStringForDisplay,
  openNote,
  renderNote,
}) => {
  const count = notes.length;

  // Tiny lists keep their native DOM — virtualization overhead isn't
  // worth it under VIRTUALIZE_THRESHOLD rows and accessible HTML
  // tables print / screen-read better.
  if (count < VIRTUALIZE_THRESHOLD) {
    if (view === 'cards') {
      return (
        <div className="space-y-3" data-testid="notes-card-list-unvirtualized">
          {notes.map((note) => (
            <React.Fragment key={note.id}>
              {renderNote(note, note.is_locked || note.status === 'locked')}
            </React.Fragment>
          ))}
        </div>
      );
    }
    // Table fallback
    return (
      <div className="overflow-x-auto" data-testid="notes-table-list-unvirtualized">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left p-2 font-medium">Client</th>
              <th className="text-left p-2 font-medium">Status</th>
              <th className="text-left p-2 font-medium">Note Type</th>
              <th className="text-left p-2 font-medium">Provider</th>
              <th className="text-left p-2 font-medium">DOS</th>
            </tr>
          </thead>
          <tbody>
            {notes.map((note) => {
              const { isLocked, clientDisplayName, noteProvider } = getRowDisplayValues(
                note, clientsByIdMap, usersByIdMap, formatClientName,
              );
              return (
                <tr
                  key={note.id}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                  onClick={() => openNote(note)}
                >
                  <td className="p-2 font-medium">
                    {clientDisplayName
                      ? clientDisplayName
                      : <span className="text-red-600">⚠️ No Client</span>}
                    {note.is_group_note && <Badge className="ml-1 text-xs">GROUP</Badge>}
                  </td>
                  <td className="p-2">
                    <Badge className={getStatusBadgeColor(note.status)}>
                      {formatStatusLabel(note)}
                    </Badge>
                    {isLocked && <Badge variant="outline" className="ml-1 text-xs">🔒</Badge>}
                  </td>
                  <td className="p-2">{note.template_name || note.title || 'Untitled'}</td>
                  <td className="p-2">{noteProvider?.full_name || 'Unknown'}</td>
                  <td className="p-2">{formatDateStringForDisplay(note.date_of_service || note.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // ── Virtualized path ────────────────────────────────────────────
  const rowHeight = view === 'cards' ? CARD_ROW_PX : TABLE_ROW_PX;
  const height = rowHeightFor(count, rowHeight);

  if (view === 'cards') {
    return (
      <div data-testid="notes-card-list-virtualized" data-row-count={count}>
        <List
          rowCount={count}
          rowHeight={rowHeight}
          defaultHeight={height}
          rowComponent={CardRow}
          rowProps={{ notes, renderNote }}
          overscanCount={4}
          style={{ height }}
        />
      </div>
    );
  }

  // Table view with virtualization — we still print a fixed header
  // outside the virtualized list so the column titles stay visible.
  return (
    <div data-testid="notes-table-list-virtualized" data-row-count={count}>
      <div className="overflow-x-auto">
        <div
          style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.4fr 1.4fr 1fr' }}
          className="border-b bg-gray-50 text-sm font-medium"
        >
          <div className="p-2 text-left">Client</div>
          <div className="p-2 text-left">Status</div>
          <div className="p-2 text-left">Note Type</div>
          <div className="p-2 text-left">Provider</div>
          <div className="p-2 text-left">DOS</div>
        </div>
        <List
          rowCount={count}
          rowHeight={rowHeight}
          defaultHeight={height}
          rowComponent={TableRow}
          rowProps={{
            notes,
            clientsByIdMap,
            usersByIdMap,
            formatClientName,
            formatStatusLabel,
            getStatusBadgeColor,
            formatDateStringForDisplay,
            openNote,
          }}
          overscanCount={6}
          style={{ height }}
        />
      </div>
    </div>
  );
};

export default VirtualizedNotesList;
